// TODO: delete when new customize UI enabled for everyone
import { action, extendObservable, reaction } from 'mobx';
import React from 'react';
import PropTypes from 'prop-types';
import { apiCall } from '../API';
import AsyncActionStore from './AsyncActionStore';
import FlagStore from './FlagStore';
import InputStore from './InputStore';
import EditCustomPageContentStore from './EditCustomPageContentStore';
import RegistrationNoticeStore from './RegistrationNoticeStore';

export const DEFAULT_CONTENT_FILTERING = {
    logo: '',
    logoName: '',
    headline: 'This site has been blocked',
    headlineIcon: true,
    background: true,
    message: `
<p>
    The website you have requested has been blocked in accordance with company policy.
</p>`,
};

export const DEFAULT_REGISTRATION_NOTICE = {
    logo: '',
    logoName: '',
    headline: 'Please review',
    headlineIcon: true,
    background: true,
    message: '',
};


export const DEFAULT_MALICIOUS_THREAT = {
    logo: '',
    logoName: '',
    headline: 'Website Has Been Blocked!',
    headlineIcon: true,
    background: true,
    message: `
<p>
    The web page you are attempting to access has been classified as malicious.
    This classification is determined by direct analysis of the web page.
    Although an entire website may be blocked as malicious,
    it is very common for a single page on a valid website to be blocked.
</p>
<p>
    Your organization has enabled this technology to protect you, your system,
    and the organization from harm. Blocked pages contain material such as:
</p>
<p>
    <strong>Credential Theft:</strong> A page may be designed to look like a valid financial
    institution, a well-known organization, or an otherwise trusted source. The page is
    requesting a login and/or password for malicious purposes.
</p>
<p>
    <strong>Malware:</strong> A page may contain files or other malicious material which are
    intended to harm your system or organization. The malicious material may contain a virus,
    an installation program, or it may expose a vulnerability in a program which exists on your system.
</p>`,
};

const getResizedImageDataUrl = (file) => new Promise((resolve) => {
    const reader = new window.FileReader();
    const canvas = document.createElement('canvas');
    const img = document.createElement('img');
    reader.onload = (e) => {
        img.onload = () => {
            let ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);

            let width = img.width;
            let height = img.height;
            if (height > 150) {
                width *= 150 / height;
                height = 150;
            }
            canvas.width = width;
            canvas.height = height;
            ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/png'));
        };
        img.src = e.target.result;
    };
    reader.readAsDataURL(file);
});

export class CustomPageEditStore {
    static PropType = PropTypes.shape({
        saveStatus: AsyncActionStore.PropType.isRequired,
        enabledStore: FlagStore.PropType.isRequired,
        fileInputKey: PropTypes.number.isRequired,
        logoName: PropTypes.string.isRequired,
        logoStore: InputStore.PropType.isRequired,
        logoMimeError: FlagStore.PropType.isRequired,
        headlineStore: InputStore.PropType.isRequired,
        headlineIconStore: FlagStore.PropType.isRequired,
        backgroundStore: FlagStore.PropType.isRequired,
        editCustomPageContent: EditCustomPageContentStore.PropType.isRequired,
        previewToggle: FlagStore.PropType.isRequired,
        hasChanged: PropTypes.func.isRequired,
        setValue: PropTypes.func.isRequired,
        getContent: PropTypes.func.isRequired,
        getValue: PropTypes.func.isRequired,
        clearLogo: PropTypes.func.isRequired,
        onFileInputChange: PropTypes.func.isRequired,
        openFileInput: PropTypes.func.isRequired,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class CustomPageEditStore */{
            type: null,
            defaultValue: {},
            saveStatus: new AsyncActionStore(),
            enabled: false,
            enabledStore: new FlagStore(false),
            fileInputRef: React.createRef(),
            fileInputKey: 1,
            logoName: '',
            logo: '',
            logoStore: new InputStore(),
            logoMimeError: new FlagStore(false),
            headline: '',
            headlineStore: new InputStore(),
            headlineIcon: true,
            headlineIconStore: new FlagStore(true),
            background: true,
            backgroundStore: new FlagStore(true),
            message: '',
            editCustomPageContent: new EditCustomPageContentStore(),
            previewToggle: new FlagStore(false),

            hasChanged() {
                return !this.enabledStore.equals(this.enabled)
                    || !this.logoStore.equals(this.logo)
                    || !this.headlineStore.equals(this.headline)
                    || !this.headlineIconStore.equals(this.headlineIcon)
                    || !this.backgroundStore.equals(this.background)
                    || this.editCustomPageContent.hasChanged();
            },
        }, data);

        this.headlineStore.setValidator(() => false);

        reaction(() => this.enabledStore.value, (newValue) => newValue && this.previewToggle.setValue(newValue));
    }

    setValue = action(({ enabled, content }) => {
        this.enabled = enabled;
        this.enabledStore.setValue(enabled);

        const data = JSON.parse(content);
        this.logo = data.logo;
        this.logoStore.setValue(data.logo);
        this.logoName = data.logoName;
        this.headline = data.headline;
        this.headlineStore.setValue(data.headline);
        this.headlineIcon = data.headlineIcon;
        this.headlineIconStore.setValue(data.headlineIcon);
        this.background = data.background;
        this.backgroundStore.setValue(data.background);
        this.message = data.message;
        this.editCustomPageContent.setValue(data.message);
    });

    getContent() {
        return {
            logo: this.logoStore.value,
            logoName: this.logoName,
            headline: this.headlineStore.value,
            headlineIcon: this.headlineIconStore.value,
            background: this.backgroundStore.value,
            message: this.editCustomPageContent.messageStore.value,
        };
    }

    getValue() {
        return {
            type: this.type,
            enabled: this.enabledStore.value,
            content: JSON.stringify(this.getContent()),
        };
    }

    clearLogo = action((resetNode) => {
        this.logoName = '';
        this.logoStore.setValue('');
        this.logoMimeError.setFalse();
        if (resetNode) {
            this.fileInputKey += 1;
        }
    });

    onFileInputChange = action((event) => {
        this.clearLogo();
        if (event.target.files[0]) {
            const file = event.target.files[0];
            if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
                this.logoMimeError.show();
            } else {
                this.logoName = file.name;
                getResizedImageDataUrl(file).then(this.logoStore.setValue);
            }
        }
    });

    openFileInput = () => {
        if (this.fileInputRef.current) {
            this.fileInputRef.current.click();
        }
    };
}

export default class CustomPagesStore {
    static PropType = PropTypes.shape({
        loadStatus: AsyncActionStore.PropType.isRequired,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class CustomPagesStore */{
            apiList: apiCall('/console/custom_pages/list'),
            apiSave: apiCall('/console/custom_pages/save'),
            loadStatus: new AsyncActionStore(),
            contentFiltering: new CustomPageEditStore({
                type: 'CONTENT_FILTERING',
                defaultValue: {
                    enabled: false,
                    content: JSON.stringify(DEFAULT_CONTENT_FILTERING),
                },
            }),
            maliciousThreat: new CustomPageEditStore({
                type: 'MALICIOUS_THREAT',
                defaultValue: {
                    enabled: false,
                    content: JSON.stringify(DEFAULT_MALICIOUS_THREAT),
                },
            }),
            registrationNotice: new RegistrationNoticeStore({
                type: 'REGISTRATION_NOTICE',
                defaultValue: {
                    enabled: false,
                    content: JSON.stringify(DEFAULT_REGISTRATION_NOTICE),
                },
            }),
        }, data);
        reaction(() => this.registrationNotice.submitted, () => {
            if (this.registrationNotice.submitted) {
                this.save(this.registrationNotice).then(() => {
                    this.registrationNotice.onSaveSuccess();
                }).catch((e) => {
                    this.registrationNotice.onSaveFailed(e);
                }).finally(() => {
                    this.registrationNotice.submitted = false;
                    this.registrationNotice.isModalOpen.setFalse();
                });
            }
        });
    }

    load = action(() => {
        if (!this.loadStatus.isLoading) {
            this.loadStatus.setLoading(this.apiList())
                .then((res) => {
                    [this.contentFiltering, this.maliciousThreat, this.registrationNotice].forEach((store) =>
                        store.setValue(res.find((cp) => store.type === cp.type) || store.defaultValue));
                    return res;
                });
        }
        return this.loadStatus.promise;
    });

    save = action((store) => {
        if (!store.saveStatus.isLoading) {
            const data = store.getValue();
            store.saveStatus.setLoading(this.apiSave(data).then(() => {
                store.setValue(data);
            }));
        }
        return store.saveStatus.promise;
    });
}
